import { useEffect } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = (v) =>
  v == null || isNaN(v)
    ? '—'
    : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

// Liste des avantages CAE
const AVANTAGES_CAE = [
  { icone: '🛡️', texte: 'Protection sociale complète (chômage, retraite, AT/MP)' },
  { icone: '👥', texte: 'Accompagnement personnalisé de votre activité' },
  { icone: '📋', texte: 'Simplicité administrative (gestion paie, compta)' },
  { icone: '🤝', texte: 'Mutualisation des moyens et des compétences' },
  { icone: '🌐', texte: 'Participation à la vie coopérative d\'un réseau d\'entrepreneurs solidaires' },
];

// Données de protection sociale (résumé pour l'impression)
const PROTECTIONS = [
  { label: 'Assurance chômage', cae: '✅ Complète',              micro: '❌ Aucune',     ei: '❌ Aucune',     sasu: '❌ Aucune' },
  { label: 'Retraite',          cae: '✅ Base + complémentaire', micro: '⚠️ Base seule', ei: '⚠️ Limitée',   sasu: '✅ Base + complémentaire' },
  { label: 'Accident travail',  cae: '✅ Couvert',               micro: '⚠️ Non couvert',ei: '⚠️ Non couvert',sasu: '⚠️ Non couvert' },
  { label: 'Maladie',           cae: '✅ Complète',              micro: '⚠️ Partielle',  ei: '⚠️ Partielle', sasu: '✅ Complète' },
  { label: 'Maternité/paternité',cae: '✅ Complète',             micro: '⚠️ Partielle',  ei: '⚠️ Partielle', sasu: '✅ Complète' },
];

// Labels des charges saisies
const LABELS_CHARGES = {
  matieres_premieres:    'Matières premières / sous-traitance',
  indemnites_km:         'Indemnités kilométriques',
  repas_affaires:        'Repas d\'affaires',
  logiciel_formation:    'Logiciel formation professionnelle',
  label_bio:             'Label Bio',
  logiciels:             'Logiciels et abonnements',
  compte_bancaire:       'Compte bancaire',
  assurance_rcp:         'Assurance RCP',
  assurance_decennale:   'Assurance décennale',
  logiciel_compta:       'Logiciel comptabilité',
  comptable:             'Expert-comptable',
  cet_cvae_cfe:          'CET / CFE',
  mutuelle_prevoyance:   'Mutuelle / prévoyance',
};

// ---------------------------------------------------------------------------
// Composant document d'impression
// ---------------------------------------------------------------------------
function PrintDocument({ data, resultats, configCAE }) {
  const cae  = configCAE?.cae_info ?? {};
  const r    = resultats ?? {};
  const rCAE  = r.resultatCAE;
  const rMicro = r.resultatMicro;
  const rEI   = r.resultatEI;
  const rSASU  = r.resultatSASU;
  const charges = data?.chargesProfessionnellesHorsCAE ?? {};
  const chargesCAE = data?.chargesProfessionnelles ?? {};
  const chargesAffiches = Object.entries({ ...chargesCAE, ...charges })
    .filter(([, v]) => v && Number(v) > 0)
    .map(([k, v]) => ({ label: LABELS_CHARGES[k] ?? k, valeur: Number(v) }));

  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  const LigneResultat = ({ label, valeur, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #e5e7eb', fontWeight: bold ? '700' : '400' }}>
      <span style={{ color: '#374151' }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(valeur)}/mois</span>
    </div>
  );

  const cellStyle = (bg) => ({
    padding: '6px 10px',
    backgroundColor: bg,
    border: '1px solid #d1d5db',
    fontSize: '12px',
  });

  return (
    <div className="hidden print:block" style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', color: '#111827', maxWidth: '900px', margin: '0 auto' }}>

      {/* ── EN-TÊTE ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #00A651', paddingBottom: '12px', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: '800', color: '#00A651' }}>
            {cae.nom || 'Simulateur CAE'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            Coopérative d'Activités et d'Emploi
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Simulation financière</div>
          <div>Date : {today}</div>
          {cae.email_contact && <div>{cae.email_contact}</div>}
          {cae.telephone && <div>{cae.telephone}</div>}
        </div>
      </div>

      {/* ── ÉTAPE 1 : DONNÉES SAISIES ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#00A651', borderBottom: '2px solid #00A651', paddingBottom: '4px', marginBottom: '10px' }}>
          1. Données de la simulation
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={cellStyle('#f9fafb')}>Secteur d'activité</td>
              <td style={cellStyle('#ffffff')}><strong>{data?.metier || '—'}</strong></td>
              <td style={cellStyle('#f9fafb')}>CA mensuel</td>
              <td style={cellStyle('#ffffff')}>
                <strong>{fmt(data?.caHT)}</strong> HT
                {data?.isTTC && <span style={{ color: '#6b7280', marginLeft: '6px' }}>(saisi en TTC)</span>}
              </td>
            </tr>
          </tbody>
        </table>
        {chargesAffiches.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Charges professionnelles renseignées :</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {chargesAffiches.map(({ label, valeur }) => (
                  <tr key={label}>
                    <td style={{ ...cellStyle('#f9fafb'), width: '50%' }}>{label}</td>
                    <td style={cellStyle('#ffffff')}>{fmt(valeur)}/mois</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ÉTAPE 2 : RÉSULTATS FINANCIERS ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#00A651', borderBottom: '2px solid #00A651', paddingBottom: '4px', marginBottom: '10px' }}>
          2. Résultats financiers comparatifs
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ ...cellStyle('#f0fdf4'), textAlign: 'left' }}></th>
              <th style={{ ...cellStyle('#f0fdf4'), textAlign: 'center', color: '#15803d' }}>CAE — Salarié·e</th>
              <th style={{ ...cellStyle('#eff6ff'), textAlign: 'center', color: '#1e40af' }}>Micro-entreprise</th>
              <th style={{ ...cellStyle('#faf5ff'), textAlign: 'center', color: '#7e22ce' }}>EI / EURL</th>
              <th style={{ ...cellStyle('#eef2ff'), textAlign: 'center', color: '#4338ca' }}>SASU</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle('#f9fafb')}>CA / base de calcul</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(data?.caHT)}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(data?.caTTC ?? data?.caHT)}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(data?.caHT)}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(data?.caHT)}</td>
            </tr>
            <tr>
              <td style={cellStyle('#f9fafb')}>Marge économique</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(rCAE?.marge_economique)}</td>
              <td style={{ ...cellStyle('#f5f5f5'), textAlign: 'center', color: '#9ca3af' }}>—</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(rEI?.marge_economique)}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>{fmt(rSASU?.marge_economique)}</td>
            </tr>
            <tr>
              <td style={cellStyle('#f9fafb')}>Cotisations sociales</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', color: '#dc2626' }}>{rCAE ? `− ${fmt(rCAE.cotisations_totales ?? (rCAE.charges_patronales + rCAE.cotisations_salariales))}` : '—'}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', color: '#dc2626' }}>{rMicro?.incompatible ? 'N/A' : rMicro ? `− ${fmt(rMicro.cotisations_sociales)}` : '—'}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', color: '#dc2626' }}>{rEI ? `− ${fmt(rEI.cotisations_sociales)}` : '—'}</td>
              <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', color: '#dc2626' }}>{rSASU ? `− ${fmt((rSASU.charges_patronales ?? 0) + (rSASU.cotisations_salariales ?? 0))}` : '—'}</td>
            </tr>
            <tr style={{ fontWeight: '700', backgroundColor: '#f0fdf4' }}>
              <td style={{ ...cellStyle('#f0fdf4'), fontWeight: '700' }}>Revenu net avant impôt</td>
              <td style={{ ...cellStyle('#dcfce7'), textAlign: 'right', color: '#15803d' }}>{fmt(rCAE?.salaire_net_avant_pas)}</td>
              <td style={{ ...cellStyle('#dbeafe'), textAlign: 'right', color: '#1e40af' }}>
                {rMicro?.incompatible ? <span style={{ color: '#dc2626', fontSize: '11px' }}>Incompatible</span> : fmt(rMicro?.revenu_net_avant_pas)}
              </td>
              <td style={{ ...cellStyle('#ede9fe'), textAlign: 'right', color: '#7e22ce' }}>{fmt(rEI?.remuneration_nette_avant_pas)}</td>
              <td style={{ ...cellStyle('#e0e7ff'), textAlign: 'right', color: '#4338ca' }}>{fmt(rSASU?.salaire_net_avant_pas)}</td>
            </tr>
            {(rCAE?.montant_total_verse !== rCAE?.salaire_net_avant_pas || rMicro?.revenu_reel_disponible !== rMicro?.revenu_net_avant_pas) && (
              <tr>
                <td style={cellStyle('#f9fafb')}>Montant total disponible</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', fontWeight: '600', color: '#15803d' }}>{fmt(rCAE?.montant_total_verse)}</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'right', fontWeight: '600', color: '#1e40af' }}>
                  {rMicro?.incompatible ? '—' : fmt(rMicro?.revenu_reel_disponible)}
                </td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>—</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'right' }}>—</td>
              </tr>
            )}
          </tbody>
        </table>
        {rMicro?.incompatible && (
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#dc2626', padding: '4px 8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px' }}>
            ⚠️ Micro-entreprise incompatible : CA annuel ({fmt((data?.caTTC ?? data?.caHT) * 12)}) dépasse le seuil de franchise TVA ({fmt(rMicro.incompatibilite?.seuil)}/an).
          </div>
        )}
      </div>

      {/* ── ÉTAPE 3 : PROTECTION SOCIALE ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#00A651', borderBottom: '2px solid #00A651', paddingBottom: '4px', marginBottom: '10px' }}>
          3. Protection sociale comparée
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ ...cellStyle('#f9fafb'), textAlign: 'left' }}>Couverture</th>
              <th style={{ ...cellStyle('#f0fdf4'), textAlign: 'center', color: '#15803d' }}>CAE</th>
              <th style={{ ...cellStyle('#eff6ff'), textAlign: 'center' }}>Micro</th>
              <th style={{ ...cellStyle('#faf5ff'), textAlign: 'center' }}>EI / EURL</th>
              <th style={{ ...cellStyle('#eef2ff'), textAlign: 'center' }}>SASU</th>
            </tr>
          </thead>
          <tbody>
            {PROTECTIONS.map(({ label, cae, micro, ei, sasu }) => (
              <tr key={label}>
                <td style={cellStyle('#f9fafb')}>{label}</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'center' }}>{cae}</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'center' }}>{micro}</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'center' }}>{ei}</td>
                <td style={{ ...cellStyle('#ffffff'), textAlign: 'center' }}>{sasu}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
          ✅ Couverture complète — ⚠️ Couverture partielle ou optionnelle — ❌ Absence de couverture
        </div>
      </div>

      {/* ── ÉTAPE 4 : POURQUOI REJOINDRE LA CAE ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#00A651', borderBottom: '2px solid #00A651', paddingBottom: '4px', marginBottom: '10px' }}>
          4. Pourquoi rejoindre {cae.nom || 'la CAE'} ?
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            {AVANTAGES_CAE.map(({ icone, texte }, i) => (
              <tr key={i}>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid #e5e7eb', width: '28px', textAlign: 'center' }}>{icone}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid #e5e7eb', color: '#111827' }}>{texte}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(configCAE?.urls?.reunion_information || configCAE?.cae_info?.url_infocoll) && (
          <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '12px' }}>
            📅 <strong>Réunion d'information :</strong>{' '}
            {configCAE?.urls?.reunion_information || configCAE?.cae_info?.url_infocoll}
          </div>
        )}
      </div>

      {/* ── CONTACT ── */}
      <div style={{ borderTop: '2px solid #00A651', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#00A651', marginBottom: '6px' }}>5. Nous contacter</div>
          {cae.nom && <div style={{ fontWeight: '600' }}>{cae.nom}</div>}
          {cae.adresse && (
            <div style={{ fontSize: '12px', color: '#374151' }}>
              {cae.adresse.rue} — {cae.adresse.code_postal} {cae.adresse.ville}
            </div>
          )}
          {cae.telephone && <div style={{ fontSize: '12px' }}>📞 {cae.telephone}</div>}
          {cae.email_contact && <div style={{ fontSize: '12px' }}>✉️ {cae.email_contact}</div>}
          {cae.url_site && <div style={{ fontSize: '12px' }}>🌐 {cae.url_site}</div>}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', maxWidth: '320px', textAlign: 'right' }}>
          ⚠️ Les calculs fournis sont indicatifs et basés sur les barèmes 2025. Ils ne se substituent pas aux décomptes officiels de l'URSSAF et ne constituent pas un engagement contractuel.
        </div>
      </div>
    </div>
  );
}

PrintDocument.propTypes = {
  data: PropTypes.object,
  resultats: PropTypes.object,
  configCAE: PropTypes.object,
};

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------
function Step4CallToAction({ data, resultats, configCAE, onReset, onPrevious }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const urlInfocoll = configCAE?.urls?.reunion_information || configCAE?.cae_info?.url_infocoll || '#';

  return (
    <>
      {/* Document d'impression — invisible en mode normal */}
      <PrintDocument data={data} resultats={resultats} configCAE={configCAE} />

      {/* Interface normale — masquée à l'impression */}
      <div className="max-w-4xl mx-auto print:hidden">
        <div className="bg-white rounded-xl shadow-card p-6 md:p-8">
          {/* En-tête */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              🎯 Prêt·e à franchir le cap ?
            </h2>
            <p className="text-xl text-gray-600">
              Rejoignez <strong className="text-primary-700">
                {configCAE?.cae_info?.nom || 'notre CAE'}
              </strong> et bénéficiez de :
            </p>
          </div>

          {/* Liste des avantages */}
          <div className="mb-8 space-y-3">
            {AVANTAGES_CAE.map((avantage, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <span className="text-3xl flex-shrink-0">{avantage.icone}</span>
                <span className="text-lg text-gray-800 pt-1">{avantage.texte}</span>
              </div>
            ))}
          </div>

          {/* Bouton réunion d'information */}
          <div className="mb-8">
            <a
              href={urlInfocoll}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-8 py-5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold text-xl rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-center"
            >
              📅 PARTICIPER À UNE RÉUNION D'INFORMATION
            </a>
          </div>

          {/* Séparateur */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 border-t border-gray-300" />
            <span className="text-gray-500 font-medium">OU</span>
            <div className="flex-1 border-t border-gray-300" />
          </div>

          {/* Impression des résultats */}
          <div className="space-y-3">
            <button
              onClick={() => window.print()}
              className="w-full px-8 py-4 bg-secondary-600 hover:bg-secondary-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>IMPRIMER LES RÉSULTATS</span>
            </button>
          </div>

          {/* Bouton recommencer */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={onReset}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>🔄 Recommencer une simulation</span>
            </button>
          </div>

          {/* Bouton retour */}
          <div className="mt-4">
            <button
              onClick={onPrevious}
              className="w-full px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Retour à la protection sociale</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

Step4CallToAction.propTypes = {
  data: PropTypes.object.isRequired,
  resultats: PropTypes.object,
  configCAE: PropTypes.object,
  onReset: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
};

export default Step4CallToAction;
